import React, { FC } from 'react';
import { getFormattedDate } from '../../utils/utilities';
import { useTranslation } from 'react-i18next';
import { Title } from '../developers/page-components/Title';
import { CopyComponent } from '../CopyComponent';
import { useOrdersContext } from '../../store/order/order-context';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../merchants/RelatedEntityNameLink';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { WhisperComponent } from '../developers/page-components/WhisperComponent';

export const OrderInformation: FC = () => {
	const { t } = useTranslation();
	const { orderContextState: { orderDetailsData }} = useOrdersContext();
	const { referenceNumber, orderPlacedDate, merchantName, siteName, merchantId, siteDescriptor } = orderDetailsData;
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	return (
		<div className="card mb-6" data-cy="order-information-block">
			<div className="card-body">
				<Title title={t('ORDERS.ORDER_INFORMATION')} additionalClass="fs-4 fw-bolder mb-2" />

				<div className="text-muted fw-bold fs-9">
					{t('ORDERS.REFERENCE_NUMBER')}
				</div>
				{
					referenceNumber
						? (
							<div className="d-flex align-items-center">
								<div className="fs-6 text-truncate">
									{referenceNumber}
								</div>
								<CopyComponent value={referenceNumber} title={t('ORDERS.MERCHANT_REFERENCE')} additionalClass="ms-2"/>
							</div>
						) : '-'
				}

				{
					!checkRoles(authUser, CLAIMS.MERCHANT_ADMIN) && (
						<>
							<div className="text-muted fw-bold fs-9">
								{t('ORDERS.MERCHANT_NAME')}
							</div>
							<div className="d-flex align-items-center">
								<div className="d-flex flex-column align-items-start">
									{
										checkRoles(authUser, CLAIMS.PARTNER_ADMIN) || checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN) ?
											<span>{merchantName}</span> :
											<RelatedEntityNameLink name={merchantName} id={merchantId} type={RELATED_ENTITY_TYPE.MERCHANT} />
									}
								</div>
							</div>
						</>
					)
				}

				{
					siteName && (
						<>
							<div className="text-muted fw-bold fs-9">
								{t('ORDERS.SITE_NAME')}
							</div>
							<div className="d-flex align-items-center">
								<div className="fs-6 text-truncate">
									{siteName}
								</div>
								<CopyComponent value={siteName} title={t('ORDERS.SITE_NAME')} additionalClass="ms-2"/>
							</div>
						</>
					)
				}

				{
					siteDescriptor && (
						<>
							<div className="d-flex align-items-center">
								<div className="text-muted fw-bold fs-9 me-2">
									{t('ORDERS.SITE_DESCRIPTOR')}
								</div>
								<WhisperComponent
									isTooltip
									whisperText='Site descriptor doesn`t include prefix or suffix'
									mainText={
										<FontAwesomeIcon
											icon={faInfoCircle}
											className="cursor-pointer"
											width={10}
										/>
									}
								/>
							</div>
							<div className="d-flex align-items-center">
								<div className="fs-6 text-truncate">
									{siteDescriptor}
								</div>
							</div>
						</>
					)
				}

				<div className="text-muted fw-bold fs-9">
					{t('ORDERS.CREATED')}
				</div>
				{
					orderPlacedDate === '0001-01-01T00:00:00'
						? '-'
						: (
							<div className="d-flex flex-column align-items-start">
									<span className="fs-6">
									  {getFormattedDate(orderPlacedDate, 'dd MMM, yyyy')}
									</span>
								<span className="fs-8">
									  {getFormattedDate(orderPlacedDate, 'hh:mm a')}
									</span>
							</div>
						)
				}
			</div>
		</div>
	);
};
